<template>
  <div class="youtube-auth">
    <b-row align-h="center">
      <b-col cols="12" sm="10" md="8" lg="6" xl="5">
        <b-card class="auth-status shadow-lg">
          <b-card-header class="auth-header">
            <div class="d-flex align-items-center">
              <div class="icon-wrapper mr-3">
                <feather-icon icon="PlayIcon" size="24" />
              </div>
              <div>
                <h5 class="mb-0 font-weight-bold">YouTube Live</h5>
                <small class="text-light">Status da Autenticação</small>
              </div>
            </div>
          </b-card-header>
          
          <b-card-body class="auth-body">
            <div class="status-container">
              <div class="status-item">
                <div class="status-info">
                  <feather-icon 
                    :icon="isAuthenticated ? 'CheckCircleIcon' : 'AlertCircleIcon'" 
                    size="18" 
                    :class="isAuthenticated ? 'text-success' : 'text-warning'"
                  />
                  <span class="status-label ml-2">Autenticação</span>
                </div>
                <b-badge 
                  :variant="isAuthenticated ? 'success' : 'warning'"
                  pill
                  class="status-badge"
                >
                  {{ isAuthenticated ? 'Conectado' : 'Desconectado' }}
                </b-badge>
              </div>
              
              <div class="status-item">
                <div class="status-info">
                  <feather-icon 
                    :icon="tokenValid ? 'ShieldCheckIcon' : 'ShieldOffIcon'" 
                    size="18" 
                    :class="tokenValid ? 'text-success' : 'text-danger'"
                  />
                  <span class="status-label ml-2">Token de Acesso</span>
                </div>
                <b-badge 
                  :variant="tokenValid ? 'success' : 'danger'"
                  pill
                  class="status-badge"
                >
                  {{ tokenValid ? 'Válido' : 'Inválido' }}
                </b-badge>
              </div>
            </div>
            
            <hr class="divider">
            
            <div class="action-buttons">
              <b-button 
                v-if="!isAuthenticated"
                @click="authenticate"
                variant="success" 
                size="lg"
                block
                class="auth-button"
              >
                <feather-icon icon="LogInIcon" size="18" class="mr-2" />
                Conectar com YouTube
              </b-button>
              <b-button 
                v-if="isAuthenticated"
                @click="authenticate"
                variant="success" 
                size="lg"
                block
                class="auth-button"
              >
                <feather-icon icon="LogInIcon" size="18" class="mr-2" />
                Conectar com outra conta
              </b-button>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { BCard, BCardHeader, BCardBody, BButton, BRow, BCol, BBadge } from 'bootstrap-vue'
import { youtubeService } from '@/services/WebinarSimulator/youtubeService'

export default {
  name: 'YouTubeAuth',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BRow,
    BCol,
    BBadge,
  },
  props: {
    isAuthenticated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tokenValid: false,
    }
  },
  watch: {
    isAuthenticated: {
      handler(newVal) {
        if (newVal) {
          this.tokenValid = true
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.checkAuthStatus()
  },
  methods: {
    async authenticate() {
      try {
        this.$toast.info('Redirecionando para autenticação...')
        await youtubeService.authenticate()
        this.tokenValid = true
        this.$emit('auth-success')
      } catch (error) {
        this.$toast.error(`Erro na autenticação: ${error.message}`)
      }
    },
    
    async refreshToken() {
      try {
        await youtubeService.refreshToken()
        this.tokenValid = true
        this.$toast.success('Token renovado com sucesso!')
      } catch (error) {
        this.$toast.error(`Erro ao renovar token: ${error.message}`)
      }
    },
    
    checkAuthStatus() {
      if (!this.isAuthenticated) {
        const token = localStorage.getItem('youtube_token')
        if (token) {
          this.tokenValid = youtubeService.constructor.isTokenValid(token)
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.youtube-auth {
  padding: 2rem 0;
  
  .auth-status {
    border: none;
    border-radius: 16px;
    overflow: hidden;
    transition: all 0.3s ease;
    background: var(--dk-bg-card, white);
    max-width: 100%;
    width: 100%;
    
    &:hover {
      transform: translateY(-2px);
    }
  }
  
  .auth-header {
    background: rgb(36, 36, 36);
    color: white;
    border: none;
    padding: 1.5rem;
    
    .icon-wrapper {
      width: 48px;
      height: 40px;
      background: rgb(214, 2, 2);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    h5 {
      color: white;
    }
    
    @media (max-width: 575.98px) {
      padding: 1rem;
      
      .icon-wrapper {
        width: 36px;
        height: 36px;
      }
      
      h5 {
        font-size: 1.1rem;
      }
      
      small {
        font-size: 0.85rem;
      }
    }
  }
  
  .auth-body {
    padding: 2rem;
    
    @media (max-width: 575.98px) {
      padding: 1rem;
    }
  }
  
  .status-container {
    margin-bottom: 1.5rem;
  }
  
  .status-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    margin-bottom: 0.5rem;
    background: var(--dk-bg-page, #f8f9fa);
    border-radius: 12px;
    transition: all 0.3s ease;
    
    &:hover {
      background: #e9ecef;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .status-info {
      display: flex;
      align-items: center;
      
      .status-label {
        font-weight: 500;
        color: var(--dk-text-secondary, #495057);
        font-size: 0.95rem;
      }
    }
    
    .status-badge {
      font-size: 0.8rem;
      padding: 0.5rem 1rem;
      font-weight: 600;
    }
    
    @media (max-width: 575.98px) {
      padding: 0.5rem;
      
      .status-label {
        font-size: 0.85rem;
      }
      
      .status-badge {
        font-size: 0.7rem;
        padding: 0.4rem 0.7rem;
      }
    }
  }
  
  .divider {
    margin: 2rem 0;
    border: none;
    height: 1px;
    background: #dee2e6;
    
    @media (max-width: 575.98px) {
      margin: 1rem 0;
    }
  }
  
  .action-buttons {
    .auth-button, .refresh-button {
      padding: 0.875rem 2rem;
      border-radius: 12px;
      font-weight: 600;
      transition: all 0.3s ease;
      
      &:hover {
        transform: translateY(-2px);
      }
      
      @media (max-width: 575.98px) {
        padding: 0.6rem 1rem;
        font-size: 0.95rem;
      }
    }
    
    .auth-button {
      background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
      border: none;
      color: white;
      
      &:hover {
        background: linear-gradient(135deg, #218838 0%, #1abc9c 100%);
      }
    }
    
    .refresh-button {
      background: transparent;
      border: 2px solid #007bff;
      color: #007bff;
      
      &:hover {
        background: #007bff;
        color: white;
      }
    }
  }
}
</style>